import { TFunction } from 'react-i18next';

interface ErrorObject {
    prefix: string;
    translationKey: string;
    params: string[];
}
export const recursiveAddTranslationToErrors = (
    schema: Record<string, any>,
    t: TFunction<string, unknown>
) => {
    for (const key in schema) {
        if (!schema[key]) {
            continue;
        }
        if (schema[key]?._errors) {
            schema[key] = translate(schema[key], t);
        }
        if (Array.isArray(schema[key])) {
            schema[key].forEach((element: any) => {
                if (element._errors) {
                    element = translate(element, t);
                }
                if (element.constructor.toString().indexOf(Object) !== -1) {
                    recursiveAddTranslationToErrors(element, t);
                }
            });
        }
        if (schema[key]?.constructor.toString().indexOf(Object) !== -1) {
            recursiveAddTranslationToErrors(schema[key], t);
        }
    }
};

// Backend appends "; Condition: <expr>" to some validation messages
// (older builds use the "Condidtion:" typo). Strip it so the bare key
// matches the translation in form-content.json.
const CONDITION_MARKERS = ['; Condition: ', '; Condidtion: '];

const stripGeneratedCondition = (
    translationKey: string
): { key: string; condition: string | null } => {
    for (const marker of CONDITION_MARKERS) {
        const i = translationKey.indexOf(marker);
        if (i >= 0) {
            return {
                key: translationKey.slice(0, i),
                condition: translationKey.slice(i + marker.length),
            };
        }
    }
    return { key: translationKey, condition: null };
};

// Logs the stripped condition to the console for QA. Includes the field
// label when _label is on the error node — DevTools has no UI context.
const logEfxCondition = (
    el: any,
    key: string,
    condition: string,
    t: TFunction<string, unknown>
): void => {
    const labelKey = typeof el?._label === 'string' ? el._label : '';
    const fieldLabel = labelKey ? t(labelKey) : '';

    const parts: string[] = ['[MeForms validation]'];
    if (fieldLabel) {
        parts.push(`field "${fieldLabel}"`);
    }
    parts.push(`${key} — condition: ${condition}`);

    // eslint-disable-next-line no-console
    console.log(parts.join(' '));
};

// Some server messages already carry the namespace ("form-content:Key").
// Prefixing those again yields "form-content:form-content:Key", which
// i18next renders as the raw key.
const DEFAULT_NAMESPACE = 'form-content';

const buildTranslationKey = (
    prefix: string | undefined,
    key: string
): string => {
    if (prefix == null && key.startsWith(`${DEFAULT_NAMESPACE}:`)) {
        return key;
    }
    return `${prefix ?? DEFAULT_NAMESPACE}:${key}`;
};

export const translate = (el: any, t: TFunction<string, unknown>) => {
    const translated = el._errors.map((errorObject: ErrorObject) => {
        const { key, condition } = stripGeneratedCondition(
            errorObject.translationKey
        );
        if (condition !== null) {
            logEfxCondition(el, key, condition, t);
        }
        return `${t(
            buildTranslationKey(errorObject.prefix, key),
            errorObject.params?.reduce(
                (acc, next, index) => ({
                    ...acc,
                    [`${index + 1}`]: t(next),
                }),
                {}
            )
        )}`;
    });
    return translated.filter(
        (msg: string, index: number, self: string[]) =>
            index === self.indexOf(msg)
    );
};
